import {blue} from '@mui/material/colors'
import EcosystemLayout from 'app/layouts/EcosystemLayout'
import MasterLayout from 'app/layouts/MasterLayout'
import ShoppingCartSubscriptions from 'app/modules/apps/account/ShoppingCartSubscriptions'
import DiscoveryViewResultsNotify from 'app/modules/apps/discovery/discovery-detail/DiscoveryViewResultsNotify'
import NotificationSearchList from 'app/modules/apps/discovery/NotificationSearchList'
import DirectoriesSearchList from 'app/modules/apps/entities/DirectoriesSearchList'
import {UserProfile, useAuthProvider} from 'app/modules/auth'
import {CBusiness} from 'app/modules/discovery/core'
import {ActivityLogPage} from 'app/pages/admin/ActivityLogPage'
import GraphManagementPage from 'app/pages/admin/GraphManagementPage'
import {IscoutAdminPage} from 'app/pages/admin/IscoutAdminPage'
import InviteLanding from 'app/pages/invite/InviteLanding'
import {Suspense, lazy} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {StartTracker} from 'utilities/tracker'

const SuspensedView = ({children}: {children: React.ReactNode}) => {
  const baseColor = blue[500]
  const {currentUser} = useAuthProvider()
  const userProfile = currentUser?.profile as unknown as UserProfile
  StartTracker({currentUser, userProfile})
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

const PrivateRoutes = () => {
  const url = new URL(window.location.href)

  const ProfilePage = lazy(() => import('../modules/apps/profile/ProfilePage'))
  const DiscoveryPage = lazy(() => import('../modules/apps/discovery/DiscoveryPage'))
  const DashboardPage = lazy(() => import('../modules/apps/dashboard/DashboardPage'))
  const ProjectsPage = lazy(() => import('../modules/apps/projects/ProjectsWrapperPage'))
  const BusinessesWrapper = lazy(() => import('../modules/apps/entities/BusinessesWrapper'))
  const ConceptsWrapper = lazy(() => import('../modules/apps/entities/ConceptsWrapper'))
  const PersonWrapper = lazy(() => import('../modules/apps/entities/PersonWrapper'))
  const AwardWrapper = lazy(() => import('../modules/apps/entities/AwardWrapper'))
  const OpportunityWrapper = lazy(() => import('../modules/apps/entities/OpportunityWrapper'))
  const InnovationWrapper = lazy(() => import('../modules/apps/entities/InnovationWrapper'))
  const AccountPage = lazy(() => import('../modules/apps/account/Account'))
  const SubscriptionPlansPage = lazy(() => import('../modules/apps/account/SubscriptionPlans'))
  const EcosystemCommPlansPage = lazy(() => import('../modules/apps/account/EcosystemCommPlans'))
  const SubscriptionCancelPage = lazy(() => import('../modules/apps/account/CancelPay'))
  const SubscriptionSuccessPage = lazy(() => import('../modules/apps/account/SuccessPay'))
  const EcosystemPage = lazy(() => import('../modules/apps/ecosystem/Ecosystem'))

  return (
    <Routes>
      <Route element={url.pathname.includes('ecosystem') ? <EcosystemLayout /> : <MasterLayout />}>
        {/* Redirect to Dashboard after success login/registration */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route
          path='dashboard/*'
          element={
            <SuspensedView>
              <DashboardPage />
            </SuspensedView>
          }
        />
        <Route
          path='admin'
          element={
            <SuspensedView>
              <IscoutAdminPage />
            </SuspensedView>
          }
        />
        <Route
          path='admin/graph'
          element={
            <SuspensedView>
              <GraphManagementPage />
            </SuspensedView>
          }
        />

        <Route
          path='admin/api_log'
          element={
            <SuspensedView>
              <ActivityLogPage />
            </SuspensedView>
          }
        />
        <Route
          path='discovery/*'
          element={
            <SuspensedView>
              <DiscoveryPage />
            </SuspensedView>
          }
        />
        <Route
          path='notification/searches'
          element={
            <SuspensedView>
              <NotificationSearchList />
            </SuspensedView>
          }
        />
        <Route
          path='notification/results'
          element={
            <SuspensedView>
              <DiscoveryViewResultsNotify />
            </SuspensedView>
          }
        />
        <Route
          path='businesses/*'
          element={
            <SuspensedView>
              <BusinessesWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='concepts/*'
          element={
            <SuspensedView>
              <ConceptsWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='people/*'
          element={
            <SuspensedView>
              <PersonWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='awards/*'
          element={
            <SuspensedView>
              <AwardWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='opportunities/*'
          element={
            <SuspensedView>
              <OpportunityWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='directories/*'
          element={
            <SuspensedView>
              <DirectoriesSearchList entity_type='business' classType={CBusiness} />
            </SuspensedView>
          }
        />
        <Route
          path='innovations/*'
          element={
            <SuspensedView>
              <InnovationWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='projects/*'
          element={
            <SuspensedView>
              <ProjectsPage />
            </SuspensedView>
          }
        />
        <Route
          path='profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='ecosystem/*'
          element={
            <SuspensedView>
              <EcosystemPage />
            </SuspensedView>
          }
        />

        <Route
          path='plans'
          element={
            <SuspensedView>
              <SubscriptionPlansPage />
            </SuspensedView>
          }
        />
        <Route
          path='ecoplans'
          element={
            <SuspensedView>
              <EcosystemCommPlansPage />
            </SuspensedView>
          }
        />
        <Route
          path='pay/success'
          element={
            <SuspensedView>
              <SubscriptionSuccessPage />
            </SuspensedView>
          }
        />
        <Route
          path='pay/cancel'
          element={
            <SuspensedView>
              <SubscriptionCancelPage />
            </SuspensedView>
          }
        />
        <Route
          path='invitation'
          element={
            <SuspensedView>
              <InviteLanding loggedIn={true} />
            </SuspensedView>
          }
        />
        <Route
          path='subs'
          element={
            <SuspensedView>
              <ShoppingCartSubscriptions />
            </SuspensedView>
          }
        />

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/dashboard' />} />
      </Route>
    </Routes>
  )
}

export {PrivateRoutes}
